import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Image as ImageIcon, Loader2, Sparkles, Link as LinkIcon, Globe, Building2, Monitor, Search, Check, Layers, AlertCircle, ArrowLeft, Database, CloudUpload, PenLine } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { uploadSharedImage, supabase } from '../../services/authService';
import { fetchSupabaseNewsItem } from '../../services/footballService';
import { updateNewsItem } from '../../services/adminService';
import { AIDraftResult, validateUrlPolicy, analyzeFootballArticle, translateArticle, translateArticleMulti } from '../../services/gemini';
import { fetchPexelsImages } from '../../services/pexelsService';
import { clearCache } from '../../services/storageService';
import { AIDraftModal } from '../News/AIDraftModal';
import { ConfirmModal } from '../Common/ConfirmModal';
import { FullPageLoader } from '../Common/FullPageLoader';
import { Layout } from 'lucide-react';
import { t } from '../../i18n';

interface NewsEditViewProps {
    newsId: string;
    onBack: () => void;
}

const PEXELS_KEYWORDS = ["soccer game", "stadium", "soccer player", "football fans"];

export const NewsEditView: React.FC<NewsEditViewProps> = ({ newsId, onBack }) => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const { lang } = useLanguage();
    const { showToast } = useToast();
    
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [postType, setPostType] = useState('community');
    const [linkUrl, setLinkUrl] = useState('');
    const [sourceName, setSourceName] = useState<string | null>(null);
    const [tempLink, setTempLink] = useState('');
    const [tempSource, setTempSource] = useState('');
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [showSourceInput, setShowSourceInput] = useState(false);
    const [category, setCategory] = useState('community');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [attachedImage, setAttachedImage] = useState<File | null>(null);
    const [pexelsImageUrl, setPexelsImageUrl] = useState<string | null>(null);
    const [showImageMenu, setShowImageMenu] = useState(false);
    const [showPexelsModal, setShowPexelsModal] = useState(false);
    const [isPexelsAnimating, setIsPexelsAnimating] = useState(false);
    const [pexelsImages, setPexelsImages] = useState<any[]>([]);
    const [pexelsPage, setPexelsPage] = useState(1);
    const [loadingPexels, setLoadingPexels] = useState(false);
    const [hasMorePexels, setHasMorePexels] = useState(true);
    const [pexelsError, setPexelsError] = useState<string | null>(null);
    const [currentPexelsQuery, setCurrentPexelsQuery] = useState('');
    const MAX_PEXELS_COUNT = 100; 
    const PEXELS_BATCH_SIZE = 50; 
    
    const [loading, setLoading] = useState(true);
    const [isValidating, setIsValidating] = useState(false);
    const [linkError, setLinkError] = useState<string | null>(null);
    const [isPosting, setIsPosting] = useState(false);
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [statusMsgIdx, setStatusMsgIdx] = useState(0);
    const [aiDraft, setAiDraft] = useState<AIDraftResult | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isPreviewAnimating, setIsPreviewAnimating] = useState(false);

    const titleRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageMenuRef = useRef<HTMLDivElement>(null);

    const loadNewsData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchSupabaseNewsItem(newsId, lang);
            if (data) {
                setTitle(data.title);
                setContent(data.content);
                setLinkUrl(data.url || '');
                setSourceName(data.source || null);
                setCategory(data.type || 'community');
                setPostType(data.type || 'community');
                if (data.image) setPreviewUrl(data.image);
            }
        } catch (e) { console.error(e); } finally { setLoading(false); }
    }, [newsId, lang]);

    useEffect(() => { loadNewsData(); }, [loadNewsData]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => { if (imageMenuRef.current && !imageMenuRef.current.contains(e.target as Node)) setShowImageMenu(false); };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { if (titleRef.current) { titleRef.current.style.height = 'auto'; titleRef.current.style.height = `${titleRef.current.scrollHeight}px`; } }, [title]);
    useEffect(() => { if (textareaRef.current) { textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; } }, [content]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) { 
            setAttachedImage(file); 
            setPexelsImageUrl(null); 
            setPreviewUrl(URL.createObjectURL(file)); 
            setShowImageMenu(false); 
        }
    };

    const loadPexelsBatch = useCallback(async (query: string, page: number, isInitial: boolean = false) => {
        if (loadingPexels || pexelsImages.length >= MAX_PEXELS_COUNT) return;
        setLoadingPexels(true); setPexelsError(null);
        try {
            const result = await fetchPexelsImages(query, PEXELS_BATCH_SIZE, page);
            if (isInitial) setPexelsImages(result.photos);
            else setPexelsImages(prev => [...prev, ...result.photos]);
            const currentTotal = isInitial ? result.photos.length : pexelsImages.length + result.photos.length;
            setHasMorePexels(result.photos.length === PEXELS_BATCH_SIZE && currentTotal < MAX_PEXELS_COUNT);
        } catch (e) { setPexelsError(t(lang, 'errorLoadingImages')); } finally { setLoading(false); }
    }, [loadingPexels, pexelsImages.length, lang]);

    const handlePexelsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMorePexels || loadingPexels) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop <= clientHeight + 100) {
            const nextPage = pexelsPage + 1;
            setPexelsPage(nextPage);
            loadPexelsBatch(currentPexelsQuery, nextPage);
        }
    };

    const handleOpenPexels = () => {
        setShowImageMenu(false); setShowPexelsModal(true); setPexelsPage(1); setPexelsImages([]); setPexelsError(null);
        const randomQuery = PEXELS_KEYWORDS[Math.floor(Math.random() * PEXELS_KEYWORDS.length)];
        setCurrentPexelsQuery(randomQuery);
        setTimeout(() => { setIsPexelsAnimating(true); loadPexelsBatch(randomQuery, 1, true); }, 10);
    };

    const handleClosePexels = () => { setIsPexelsAnimating(false); setTimeout(() => { setShowPexelsModal(false); setPexelsImages([]); setPexelsError(null); }, 500); };
    
    const handleSaveLink = () => {
        if (!tempLink.trim()) return;
        if (!tempLink.startsWith('https://')) {
            setLinkError(t(lang, 'linkValidationError'));
            return;
        }
        setLinkUrl(tempLink);
        setShowLinkInput(false);
        setTempLink('');
        setLinkError(null);
    };

    const handleSaveSource = () => { if (tempSource.trim()) { setSourceName(tempSource); setShowSourceInput(false); setTempSource(''); } };

    const handlePost = async () => {
        if (!user || !content.trim() || !title.trim()) return;

        // 관리자인 경우 저장 버튼 누르면 타입 선택 모달 표시
        if (user.role === 'admin' && !showTypeSelector) {
            setShowTypeSelector(true);
            return;
        }

        setIsPosting(true);
        try {
            let finalImageUrl = pexelsImageUrl || previewUrl;
            if (attachedImage) finalImageUrl = await uploadSharedImage(user.id, attachedImage);

            // 1. 초기 업데이트 (작성 언어 우선)
            const updates: any = {
                type: postType,
                source: (postType === 'magazine' || postType === 'webtoon') ? postType : (sourceName || undefined),
                url: linkUrl || undefined,
                image: finalImageUrl || undefined,
                [`title_${lang}`]: title.trim(),
                [`content_${lang}`]: content.trim(),
            };

            const { error: postError } = await supabase.from('news_list').update(updates).eq('id', newsId);
            if (postError) throw postError;

            // 2. 백그라운드 번역 및 업데이트 (비동기)
            const performBackgroundTranslation = async (id: string) => {
                try {
                    const translations = await translateArticleMulti(title, content, false);
                    if (translations) {
                        const updatePayload = {
                            title_ko: translations.ko?.title?.trim() || title.trim(),
                            title_en: translations.en?.title?.trim() || title.trim(),
                            // title_ja: translations.ja?.title?.trim() || title.trim(),
                            // title_es: translations.es?.title?.trim() || title.trim(),
                            // title_fr: translations.fr?.title?.trim() || title.trim(),
                            content_ko: translations.ko?.content?.trim() || content.trim(),
                            content_en: translations.en?.content?.trim() || content.trim(),
                            // content_ja: translations.ja?.content?.trim() || content.trim(),
                            // content_es: translations.es?.content?.trim() || content.trim(),
                            // content_fr: translations.fr?.content?.trim() || content.trim(),
                        };
                        await supabase.from('news_list').update(updatePayload).eq('id', id);
                    }
                } catch (transError) {
                    console.error("Background translation failed:", transError);
                }
            };

            // Promise에 await를 걸지 않고 백그라운드에서 실행
            performBackgroundTranslation(newsId);

            clearCache();
            showToast('success', t(lang, 'postSuccess'));
            
            // 즉시 뉴스 페이지로 이동
            setTimeout(() => {
                if (window.navigateTo) window.navigateTo('/feed');
                else onBack();
            }, 1000);
        } catch (e: any) {
            showToast('error', e.message);
        } finally {
            setIsPosting(false);
            setShowTypeSelector(false);
        }
    };

    if (loading || isAuthLoading || !user) return <FullPageLoader isLoading={true} icon={Database} />;

    return (
        <div className="min-h-screen bg-white flex flex-col relative">
            {isPosting && (
                <div className="fixed inset-0 z-[500] flex flex-col items-center justify-center bg-white/60 backdrop-blur-xl">
                    <div className="relative mb-6">
                        <PenLine className="text-rose-500 animate-[bounce_1s_infinite]" size={40} />
                        <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-rose-200/50 scale-x-0 animate-[grow-shrink_1s_infinite]"></div>
                    </div>
                    <h3 className="text-lg font-black uppercase tracking-tighter">{t(lang, 'uploadingIntel')}</h3>
                </div>
            )}
            
            <ConfirmModal 
                isOpen={showExitConfirm} 
                onClose={() => setShowExitConfirm(false)} 
                onConfirm={onBack}
                title={t(lang, 'editCancel')}
                description={t(lang, 'editCancelDesc')}
                confirmLabel={t(lang, 'confirm')}
                cancelLabel={t(lang, 'cancel')}
            />

            <ConfirmModal
                isOpen={showTypeSelector}
                onClose={() => setShowTypeSelector(false)}
                onConfirm={handlePost}
                title={t(lang, 'selectPostType')}
                description={
                    <div className="mt-4 flex flex-col gap-2">
                        <button 
                            onClick={() => setPostType('community')}
                            className={`w-full px-4 py-3 rounded-2xl text-sm font-black uppercase flex items-center justify-between transition-all ${postType === 'community' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'}`}
                        >
                            <span>Community</span>
                            {postType === 'community' && <Check size={16} />}
                        </button>
                        <button 
                            onClick={() => setPostType('magazine')}
                            className={`w-full px-4 py-3 rounded-2xl text-sm font-black uppercase flex items-center justify-between transition-all ${postType === 'magazine' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'}`}
                        >
                            <span>Magazine</span>
                            {postType === 'magazine' && <Check size={16} />}
                        </button>
                        <button 
                            onClick={() => setPostType('webtoon')}
                            className={`w-full px-4 py-3 rounded-2xl text-sm font-black uppercase flex items-center justify-between transition-all ${postType === 'webtoon' ? 'bg-slate-900 text-white shadow-lg' : 'bg-slate-50 text-slate-400'}`}
                        >
                            <span>Webtoon</span>
                            {postType === 'webtoon' && <Check size={16} />}
                        </button>
                    </div>
                }
                confirmLabel={t(lang, 'save')}
                cancelLabel={t(lang, 'cancel')}
            />

            <AIDraftModal isOpen={isPreviewOpen} isAnimating={isPreviewAnimating} draft={aiDraft} lang={lang} onClose={() => { setIsPreviewAnimating(false); setTimeout(() => setIsPreviewOpen(false), 500); }} onApply={() => { setTitle(aiDraft!.title); setContent(aiDraft!.generatedContent); setIsPreviewAnimating(false); setTimeout(() => setIsPreviewOpen(false), 500); }} />

            <header className="sticky top-0 z-40 h-[65px] px-4 flex items-center justify-between border-b border-gray-100 bg-white/95 backdrop-blur-md">
                <div className="flex items-center gap-4">
                    <button onClick={() => setShowExitConfirm(true)} className="p-2 md:hover:bg-gray-100 rounded-full transition-all"><ArrowLeft size={24} className="text-slate-900" /></button>
                    <h2 className="text-lg font-black text-slate-900 uppercase">{t(lang, 'editFeed')}</h2>
                </div>
                <button onClick={handlePost} disabled={isPosting || !content.trim() || !title.trim()} className="bg-slate-900 text-white px-6 py-2 rounded-full font-black text-xs uppercase transition-all">{t(lang, 'save')}</button>
            </header>

            <main className="flex-1 overflow-y-auto p-4 md:p-8 pb-32">
                <div className="max-w-[600px] mx-auto flex gap-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-50 shrink-0"><img src={user?.avatar} className="w-full h-full object-cover" /></div>
                    <div className="flex-1">
                        <textarea ref={titleRef} placeholder={t(lang, 'newsTitle')} rows={1} className="w-full bg-transparent border-none focus:ring-0 text-2xl font-black text-slate-900 mb-2 outline-none uppercase resize-none overflow-hidden" value={title} onChange={(e) => setTitle(e.target.value)} />
                        <textarea ref={textareaRef} placeholder={t(lang, 'whatsHappening')} className="w-full bg-transparent border-none focus:ring-0 text-lg md:text-xl font-medium text-slate-800 resize-none min-h-[300px] outline-none" value={content} onChange={(e) => setContent(e.target.value)} />
                        
                        {previewUrl && (
                            <div className="relative mt-4 mb-6 group">
                                <img src={previewUrl} className="w-full rounded-3xl border border-gray-100 shadow-sm" alt="" />
                                <button onClick={() => { setPreviewUrl(null); setAttachedImage(null); setPexelsImageUrl(null); }} className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full transition-all md:hover:bg-rose-500"><X size={16} strokeWidth={3} /></button>
                            </div>
                        )}

                        <div className="space-y-3 my-6">
                            {showLinkInput && (
                                <div className="p-2 bg-slate-50 rounded-2xl border border-rose-100 flex items-center gap-3 animate-in slide-in-from-top-2">
                                    <Globe size={18} className="text-rose-500" />
                                    <input type="text" placeholder={t(lang, 'linkPlaceholder')} className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-black text-rose-600 outline-none" value={tempLink} onChange={(e) => setTempLink(e.target.value)} />
                                    <button onClick={handleSaveLink} className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase">{isValidating ? <Loader2 className="animate-spin" size={14}/> : t(lang, 'save')}</button>
                                </div>
                            )}
                            
                            {showSourceInput && (
                                <div className="p-2 bg-slate-50 rounded-2xl border border-indigo-100 flex items-center gap-3 animate-in slide-in-from-top-2">
                                    <Building2 size={18} className="text-indigo-500" />
                                    <input type="text" placeholder={t(lang, 'sourcePlaceholder')} className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-black text-indigo-600 outline-none" value={tempSource} onChange={(e) => setTempSource(e.target.value)} />
                                    <button onClick={handleSaveSource} className="bg-slate-900 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase">{t(lang, 'save')}</button>
                                </div>
                            )}

                            {(linkUrl || sourceName) && (
                                <div className="flex flex-wrap gap-2">
                                    {linkUrl && (
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 text-rose-600 rounded-full text-[10px] font-black border border-rose-100">
                                            <Globe size={12} />
                                            <span className="truncate max-w-[150px]">{linkUrl}</span>
                                            <button onClick={() => setLinkUrl('')}><X size={12} strokeWidth={4}/></button>
                                        </div>
                                    )}
                                    {sourceName && (
                                        <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-full text-[10px] font-black border border-indigo-100">
                                            <Building2 size={12} />
                                            <span>{sourceName}</span>
                                            <button onClick={() => setSourceName(null)}><X size={12} strokeWidth={4}/></button>
                                        </div>
                                    )}
                                </div>
                            )}
                            {linkError && <p className="text-[10px] text-rose-500 font-bold px-2">{linkError}</p>}
                        </div>

                        <div className="flex items-center gap-2 mt-8 pt-4 border-t border-gray-50 relative" ref={imageMenuRef}>
                            <button onClick={() => setShowImageMenu(!showImageMenu)} className={`p-2 transition-colors ${showImageMenu ? 'text-rose-500' : 'text-slate-400 md:hover:text-rose-500'}`}><ImageIcon size={24} /></button>
                            {showImageMenu && (
                                <div className="absolute bottom-full left-0 mb-4 w-44 bg-white border border-gray-100 rounded-2xl shadow-2xl overflow-hidden z-50">
                                    <div className="p-1.5 flex flex-col gap-1">
                                        <button onClick={() => { fileInputRef.current?.click(); setShowImageMenu(false); }} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase text-slate-700 md:hover:bg-slate-50"><Monitor size={16} className="text-indigo-500" /><span>Drive</span></button>
                                        <button onClick={handleOpenPexels} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-xs font-black uppercase text-slate-700 md:hover:bg-slate-50"><Search size={16} className="text-emerald-500" /><span>Pexels</span></button>
                                    </div>
                                </div>
                            )}
                            
                            {!linkUrl && <button onClick={() => setShowLinkInput(!showLinkInput)} className={`p-2 transition-colors ${showLinkInput ? 'text-rose-500' : 'text-slate-400 md:hover:text-indigo-500'}`}><LinkIcon size={24} /></button>}
                            {!sourceName && <button onClick={() => setShowSourceInput(!showSourceInput)} className={`p-2 transition-colors ${showSourceInput ? 'text-indigo-500' : 'text-slate-400 md:hover:text-indigo-500'}`}><Building2 size={24} /></button>}

                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                        </div>
                    </div>
                </div>
            </main>

            {/* Pexels Modal */}
            {showPexelsModal && (
                <div className={`fixed inset-0 z-[300] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isPexelsAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleClosePexels}></div>
                    <div className={`relative bg-white w-full h-[85vh] md:h-[80vh] md:max-w-4xl rounded-t-[40px] md:rounded-[48px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isPexelsAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-6 md:p-8 flex items-center justify-between border-b border-slate-100 shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center"><ImageIcon size={20}/></div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'pexelsGallery')}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{t(lang, 'highFidelityAssets')}</p>
                                </div>
                            </div>
                            <button onClick={handleClosePexels} className="p-2 bg-slate-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all"><X size={20} strokeWidth={3} /></button>
                        </header>
                        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar" onScroll={handlePexelsScroll}>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {pexelsImages.map((img: any) => (
                                    <div 
                                        key={img.id} 
                                        className="aspect-video rounded-2xl overflow-hidden cursor-pointer group relative bg-slate-100"
                                        onClick={() => {
                                            setPexelsImageUrl(img.src.large);
                                            setPreviewUrl(img.src.large);
                                            setAttachedImage(null);
                                            handleClosePexels();
                                        }}
                                    >
                                        <img src={img.src.medium} className="w-full h-full object-cover transition-transform md:group-hover:scale-110" alt="" />
                                        <div className="absolute inset-0 bg-black/20 opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <Check className="text-white" size={32} strokeWidth={3} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {loadingPexels && (
                                <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-rose-500" size={24} /></div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
